<template>
  <div v-if="actions.length || announcements.length" class="notice-banner" :style="{ top: offset + 'px' }">
    <div
      v-for="action in actions"
      :key="'action-' + action.key"
      class="alert required-action"
      :class="action.blocking ? 'alert-danger' : 'alert-warning'"
      role="alert"
    >
      <strong v-if="actionCopy(action).title" class="notice-title">{{ actionCopy(action).title }}</strong>
      <span v-if="actionCopy(action).body" class="notice-body">{{ actionCopy(action).body }}</span>

      <router-link
        v-if="action.route && actionCopy(action).cta"
        :to="{ name: action.route }"
        class="btn btn-sm btn-default notice-cta"
      >
        {{ actionCopy(action).cta }}
      </router-link>
    </div>

    <div
      v-for="announcement in announcements"
      :key="'announcement-' + announcement.id"
      class="alert announcement"
      :class="levelClass(announcement.level)"
      role="alert"
    >
      <strong v-if="announcement.title" class="notice-title">{{ announcement.title }}</strong>
      <span v-if="announcement.body" class="notice-body">{{ announcement.body }}</span>

      <button
        v-if="announcement.is_dismissible"
        type="button"
        class="close notice-dismiss"
        :disabled="dismissing.indexOf(announcement.id) > -1"
        @click.prevent="dismiss(announcement)"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>
</template>

<script>
import AnnouncementService from "@/services/AnnouncementService";

const LEVEL_CLASSES = {
  info: "alert-info",
  warning: "alert-warning",
  critical: "alert-danger"
};

/**
 * The single notice slot (RR-330).
 *
 * Two different things share it on purpose:
 *  - required actions are derived from user state, cannot be dismissed, and
 *    may block navigation;
 *  - announcements are authored by an admin, are informational, and can be
 *    dismissed per user.
 *
 * Both ride along on the find-by-token payload, so neither costs a request.
 */
export default {
  name: "notice-banner",
  data() {
    return {
      dismissing: [],
      offset: 0,
      service: new AnnouncementService()
    };
  },
  mounted() {
    this.measureChrome();
    window.addEventListener("resize", this.measureChrome);
  },
  updated() {
    this.measureChrome();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measureChrome);
  },
  watch: {
    // The search bar exists on some routes and not others, and the banner does
    // not re-render on navigation by itself.
    $route() {
      this.$nextTick(this.measureChrome);
    }
  },
  computed: {
    /**
     * Both getters are empty until find-by-token has answered in this page
     * load (RR-330), so a reload never flashes notices restored from
     * localStorage — including announcements dismissed on another device.
     */
    actions() {
      return this.$store.getters.requiredActions;
    },
    announcements() {
      return this.$store.getters.announcements;
    }
  },
  methods: {
    /**
     * Where the fixed chrome ends on this page (RR-330).
     *
     * The topbar is everywhere, the search bar only on some routes, and the
     * offsets are hardcoded per page id in the theme. Measuring beats guessing:
     * the banner lands under whatever is actually on screen, and pages the
     * theme does not know about keep working.
     */
    measureChrome() {
      // Not offsetParent: that is null for every position:fixed element, which
      // is all of the chrome. A zero height is what "not on this page" looks
      // like here.
      const bottomOf = selector => {
        const rect = document.querySelector(selector)?.getBoundingClientRect();

        return rect && rect.height > 0 ? rect.bottom : 0;
      };

      this.offset = Math.max(0, bottomOf(".topbar"), bottomOf(".search"));
    },

    levelClass(level) {
      return LEVEL_CLASSES[level] ?? LEVEL_CLASSES.info;
    },

    /**
     * Copy lookup is defensive on purpose. An action key whose notices block
     * has not been translated yet must degrade to an empty banner rather than
     * throw and take the whole authenticated layout down with it.
     */
    actionCopy(action) {
      const block = this.i18n?.notices?.required_actions?.[action.key];

      return {
        title: block?.title ?? "",
        body: block?.body ?? "",
        cta: block?.cta ?? ""
      };
    },

    dismiss(announcement) {
      if (this.dismissing.indexOf(announcement.id) > -1) {
        return;
      }

      this.dismissing.push(announcement.id);

      this.service
        .dismiss(announcement.id)
        .then(() => this.$store.dispatch("dismissAnnouncement", announcement.id))
        .catch(() => {
          // Leave it on screen: a failed dismissal that silently disappeared
          // would come back on the next reload and look like a bug.
          this.dismissing = this.dismissing.filter(id => id !== announcement.id);
        });
    }
  }
};
</script>
